import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui';
import { useAppDispatch } from '../../store';
import {
    fetchBlogSortingFilters,
    fetchBlogs,
    toggleBlogStatusThunk,
    deleteBlogThunk,
    clearBlogsError,
    useBlogsList,
    useBlogsPagination,
    useBlogsSortingOptions,
    useBlogsStatusOptions,
    useBlogsLoading,
    useBlogsError,
    useBlogsTogglingIds,
    useBlogsDeletingIds,
} from '../../store/slices/blogsSlice';
import { BLOG_DEFAULT_SORT, BLOG_STATUS } from '../../constants/api.constants';
import type { BlogListItem } from '../../types/api.types';

type BlogStatusFilter = 'Active' | 'Inactive' | '';

const STATUS_COLORS: Record<string, string> = {
    [BLOG_STATUS.ACTIVE]: 'bg-[#10b981] text-white',
    [BLOG_STATUS.INACTIVE]: 'bg-[#64748b] text-white',
};

function formatDate(dateStr: string) {
    const d = new Date(dateStr);
    return {
        date: d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
        time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    };
}

export default function Blogs() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // ─── 1. Data hooks ───────────────────────────────────────────────────────
    const blogs = useBlogsList();
    const pagination = useBlogsPagination();
    const sortingOptions = useBlogsSortingOptions();
    const statusOptions = useBlogsStatusOptions();
    const loading = useBlogsLoading();
    const error = useBlogsError();
    const togglingIds = useBlogsTogglingIds();
    const deletingIds = useBlogsDeletingIds();

    // ─── 2. Local state ──────────────────────────────────────────────────────
    const [currentPage, setCurrentPage] = useState(1);
    const [currentSort, setCurrentSort] = useState<string>(BLOG_DEFAULT_SORT);
    const [selectedStatus, setSelectedStatus] = useState<BlogStatusFilter>('');
    const [showSortMenu, setShowSortMenu] = useState(false);
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const [toggleConfirmId, setToggleConfirmId] = useState<string | null>(null);

    // ─── 3. Derived values ───────────────────────────────────────────────────
    const totalPages = useMemo(() => pagination?.totalPages ?? 0, [pagination]);
    const totalCount = useMemo(() => pagination?.totalCount ?? 0, [pagination]);

    const pageStart = useMemo(
        () => (pagination ? pagination.skip + 1 : 0),
        [pagination]
    );
    const pageEnd = useMemo(
        () => (pagination ? Math.min(pagination.skip + pagination.limit, totalCount) : 0),
        [pagination, totalCount]
    );

    // ─── 4. Handlers ─────────────────────────────────────────────────────────
    const handleSortChange = useCallback((sort: string) => {
        setCurrentSort(sort);
        setCurrentPage(1);
        setShowSortMenu(false);
    }, []);

    const handleStatusFilterChange = useCallback((status: BlogStatusFilter) => {
        setSelectedStatus((prev) => (prev === status ? '' : status));
        setCurrentPage(1);
        setShowFilterMenu(false);
    }, []);

    const handleClearFilters = useCallback(() => {
        setSelectedStatus('');
        setCurrentPage(1);
        setShowFilterMenu(false);
    }, []);

    const handlePrevPage = useCallback(() => {
        setCurrentPage((prev) => Math.max(1, prev - 1));
    }, []);

    const handleNextPage = useCallback(() => {
        setCurrentPage((prev) => prev + 1);
    }, []);

    const handleEditBlog = useCallback(
        (blogId: string) => {
            navigate(`/blogs/edit/${blogId}`);
        },
        [navigate]
    );

    const handleToggleStatusClick = useCallback((blogId: string) => {
        setToggleConfirmId(blogId);
    }, []);

    const handleToggleStatusConfirm = useCallback(() => {
        if (!toggleConfirmId) return;
        dispatch(toggleBlogStatusThunk(toggleConfirmId));
        setToggleConfirmId(null);
    }, [toggleConfirmId, dispatch]);

    const handleToggleStatusCancel = useCallback(() => {
        setToggleConfirmId(null);
    }, []);

    const handleDeleteClick = useCallback((blogId: string) => {
        setDeleteConfirmId(blogId);
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (!deleteConfirmId) return;
        dispatch(deleteBlogThunk(deleteConfirmId));
        setDeleteConfirmId(null);
    }, [deleteConfirmId, dispatch]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteConfirmId(null);
    }, []);

    const handleDismissError = useCallback(() => {
        dispatch(clearBlogsError());
    }, [dispatch]);

    // ─── 5. Effects ──────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchBlogSortingFilters());
    }, [dispatch]);

    useEffect(() => {
        dispatch(
            fetchBlogs({
                page: currentPage,
                sortValue: currentSort,
                ...(selectedStatus ? { status: selectedStatus } : {}),
            })
        );
    }, [dispatch, currentPage, currentSort, selectedStatus]);

    // ─── 6. JSX ──────────────────────────────────────────────────────────────
    const blogToDelete = useMemo(
        () => blogs.find((b) => b._id === deleteConfirmId),
        [blogs, deleteConfirmId]
    );

    return (
        <div className="w-full space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-4xl font-bold text-[#1e293b] leading-tight">Blogs</h2>
                    <p className="text-lg text-[#64748b] mt-2">
                        Manage blog posts for your customers
                    </p>
                </div>
                <button
                    onClick={() => navigate('/blogs/create')}
                    className="px-6 py-3 bg-gradient-to-r from-[#0ea5e9] to-[#38bdf8] text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-200 text-base flex items-center gap-2"
                >
                    <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                        />
                    </svg>
                    New Blog Post
                </button>
            </div>

            {/* Error */}
            {error && (
                <div className="bg-[#fee2e2] border border-[#ef4444] text-[#ef4444] px-4 py-3 rounded-lg flex items-center justify-between">
                    <p className="text-sm font-medium">{error}</p>
                    <button
                        onClick={handleDismissError}
                        className="ml-4 text-[#ef4444] hover:opacity-70 transition-opacity"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>
            )}

            {/* Toolbar */}
            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
                <p className="text-sm text-[#64748b]">
                    {totalCount > 0 ? (
                        <>
                            <span className="font-semibold text-[#1e293b]">{totalCount}</span> blog
                            {totalCount !== 1 ? 's' : ''}
                        </>
                    ) : loading === 'loading' ? (
                        'Loading...'
                    ) : (
                        'No blogs found'
                    )}
                    {selectedStatus && (
                        <span className="ml-1">
                            · filtered by{' '}
                            <span className="font-medium text-[#1e293b]">{selectedStatus}</span>
                        </span>
                    )}
                </p>

                <div className="flex items-center gap-3">
                    {/* Sort dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => {
                                setShowSortMenu((prev) => !prev);
                                setShowFilterMenu(false);
                            }}
                            className="px-4 py-2.5 border border-[#e2e8f0] rounded-lg transition-all flex items-center gap-2 text-[#64748b] hover:bg-[#f8fafc] text-sm font-medium whitespace-nowrap"
                        >
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                                />
                            </svg>
                            <span className="max-w-[140px] truncate">{currentSort}</span>
                            <svg
                                className="w-3.5 h-3.5 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </button>
                        {showSortMenu && (
                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-[#e2e8f0] z-20">
                                <div className="p-2">
                                    {sortingOptions.map((opt) => (
                                        <button
                                            key={opt}
                                            onClick={() => handleSortChange(opt)}
                                            className={`w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-center justify-between text-sm ${
                                                currentSort === opt
                                                    ? 'bg-[#f1f5f9] text-[#6e64f0] font-medium'
                                                    : 'text-[#64748b] hover:bg-[#f8fafc]'
                                            }`}
                                        >
                                            {opt}
                                            {currentSort === opt && (
                                                <svg
                                                    className="w-4 h-4 flex-shrink-0"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M5 13l4 4L19 7"
                                                    />
                                                </svg>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Filter dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => {
                                setShowFilterMenu((prev) => !prev);
                                setShowSortMenu(false);
                            }}
                            className={`px-4 py-2.5 border rounded-lg transition-all flex items-center gap-2 text-sm font-medium ${
                                selectedStatus
                                    ? 'bg-[#0ea5e9] text-white border-[#0ea5e9]'
                                    : 'border-[#e2e8f0] text-[#64748b] hover:bg-[#f8fafc]'
                            }`}
                        >
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                                />
                            </svg>
                            Filter{selectedStatus && ` · ${selectedStatus}`}
                        </button>
                        {showFilterMenu && (
                            <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-[#e2e8f0] z-20">
                                <div className="p-3 border-b border-[#e2e8f0] flex items-center justify-between">
                                    <span className="text-sm font-semibold text-[#1e293b]">
                                        Status
                                    </span>
                                    {selectedStatus && (
                                        <button
                                            onClick={handleClearFilters}
                                            className="text-xs text-[#0ea5e9] hover:underline font-medium"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                                <div className="p-2">
                                    {statusOptions.map((opt) => (
                                        <button
                                            key={opt}
                                            onClick={() =>
                                                handleStatusFilterChange(opt as BlogStatusFilter)
                                            }
                                            className={`w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-center gap-2.5 text-sm ${
                                                selectedStatus === opt
                                                    ? 'bg-[#f1f5f9] text-[#1e293b] font-medium'
                                                    : 'text-[#64748b] hover:bg-[#f8fafc]'
                                            }`}
                                        >
                                            <div
                                                className={`w-2 h-2 rounded-full ${
                                                    STATUS_COLORS[opt]?.split(' ')[0] ??
                                                    'bg-[#94a3b8]'
                                                }`}
                                            />
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Loading */}
            {loading === 'loading' && (
                <div className="flex items-center justify-center py-16">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0ea5e9]" />
                </div>
            )}

            {/* Blog Table */}
            {loading !== 'loading' && (
                <Card>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#64748b] uppercase tracking-wider w-16">
                                            Cover
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#64748b] uppercase tracking-wider">
                                            Title &amp; Subtitle
                                        </th>
                                        <th className="px-6 py-4 text-center text-sm font-semibold text-[#64748b] uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#64748b] uppercase tracking-wider">
                                            Created
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#64748b] uppercase tracking-wider">
                                            Updated
                                        </th>
                                        <th className="px-6 py-4 text-center text-sm font-semibold text-[#64748b] uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#e2e8f0]">
                                    {blogs.length > 0 ? (
                                        blogs.map((blog: BlogListItem) => {
                                            const isToggling = togglingIds.includes(blog._id);
                                            const isDeleting = deletingIds.includes(blog._id);
                                            const createdAt = formatDate(blog.createdAt);
                                            const updatedAt = formatDate(blog.updatedAt);
                                            return (
                                                <tr
                                                    key={blog._id}
                                                    className="hover:bg-[#f8fafc] transition-colors"
                                                >
                                                    {/* Cover thumbnail */}
                                                    <td className="px-6 py-4">
                                                        <img
                                                            src={blog.imageUrl}
                                                            alt={blog.title}
                                                            className="w-12 h-12 rounded-lg object-cover bg-[#f1f5f9]"
                                                        />
                                                    </td>

                                                    {/* Title + Subtitle */}
                                                    <td className="px-6 py-4 max-w-xs">
                                                        <p className="text-base font-semibold text-[#1e293b] truncate">
                                                            {blog.title}
                                                        </p>
                                                        <p className="text-sm text-[#64748b] mt-0.5 line-clamp-2">
                                                            {blog.subtitle}
                                                        </p>
                                                    </td>

                                                    {/* Status */}
                                                    <td className="px-6 py-4 text-center">
                                                        <span
                                                            className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${STATUS_COLORS[blog.status] ?? 'bg-[#e2e8f0] text-[#64748b]'}`}
                                                        >
                                                            {blog.status}
                                                        </span>
                                                    </td>

                                                    {/* Created At */}
                                                    <td className="px-6 py-4">
                                                        <p className="text-base text-[#1e293b]">
                                                            {createdAt.date}
                                                        </p>
                                                        <p className="text-sm text-[#64748b] mt-0.5">
                                                            {createdAt.time}
                                                        </p>
                                                    </td>

                                                    {/* Updated At */}
                                                    <td className="px-6 py-4">
                                                        <p className="text-base text-[#1e293b]">
                                                            {updatedAt.date}
                                                        </p>
                                                        <p className="text-sm text-[#64748b] mt-0.5">
                                                            {updatedAt.time}
                                                        </p>
                                                    </td>

                                                    {/* Actions */}
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center justify-center gap-2">
                                                            {/* Edit */}
                                                            <button
                                                                onClick={() =>
                                                                    handleEditBlog(blog._id)
                                                                }
                                                                title="Edit"
                                                                className="p-2.5 rounded-lg hover:bg-[#e2e8f0] transition-colors"
                                                            >
                                                                <svg
                                                                    className="w-5 h-5 text-[#64748b]"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth={2}
                                                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                                    />
                                                                </svg>
                                                            </button>

                                                            {/* Toggle status — check-circle / slash-circle */}
                                                            <button
                                                                onClick={() =>
                                                                    handleToggleStatusClick(blog._id)
                                                                }
                                                                disabled={isToggling}
                                                                title={
                                                                    blog.status ===
                                                                    BLOG_STATUS.ACTIVE
                                                                        ? 'Deactivate'
                                                                        : 'Activate'
                                                                }
                                                                className={`p-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                                                    blog.status === BLOG_STATUS.ACTIVE
                                                                        ? 'hover:bg-[#fef9c3]'
                                                                        : 'hover:bg-[#dcfce7]'
                                                                }`}
                                                            >
                                                                {isToggling ? (
                                                                    <div className="w-5 h-5 border-2 border-[#64748b] border-t-transparent rounded-full animate-spin" />
                                                                ) : blog.status === BLOG_STATUS.ACTIVE ? (
                                                                    /* Active → click to deactivate: show a filled check-circle in green */
                                                                    <svg
                                                                        className="w-5 h-5 text-[#10b981]"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        viewBox="0 0 24 24"
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth={2}
                                                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                                        />
                                                                    </svg>
                                                                ) : (
                                                                    /* Inactive → click to activate: show an x-circle in slate */
                                                                    <svg
                                                                        className="w-5 h-5 text-[#94a3b8]"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        viewBox="0 0 24 24"
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth={2}
                                                                            d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                                        />
                                                                    </svg>
                                                                )}
                                                            </button>

                                                            {/* Delete */}
                                                            <button
                                                                onClick={() =>
                                                                    handleDeleteClick(blog._id)
                                                                }
                                                                disabled={isDeleting}
                                                                title="Delete"
                                                                className="p-2.5 rounded-lg hover:bg-[#fee2e2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                {isDeleting ? (
                                                                    <div className="w-5 h-5 border-2 border-[#ef4444] border-t-transparent rounded-full animate-spin" />
                                                                ) : (
                                                                    <svg
                                                                        className="w-5 h-5 text-[#ef4444]"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        viewBox="0 0 24 24"
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth={2}
                                                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                                        />
                                                                    </svg>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={6} className="px-6 py-16 text-center">
                                                <div className="flex flex-col items-center justify-center">
                                                    <div className="w-14 h-14 rounded-2xl bg-[#f1f5f9] flex items-center justify-center mb-4">
                                                        <svg
                                                            className="w-7 h-7 text-[#94a3b8]"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={1.5}
                                                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <p className="text-base font-semibold text-[#64748b]">
                                                        No blog posts found
                                                    </p>
                                                    <p className="text-sm text-[#94a3b8] mt-1">
                                                        {selectedStatus
                                                            ? 'Try clearing the filter'
                                                            : 'Create your first blog post'}
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Pagination */}
            {pagination && totalCount > 0 && (
                <div className="flex items-center justify-between text-sm text-[#64748b]">
                    <p>
                        Showing{' '}
                        <span className="font-medium text-[#1e293b]">
                            {pageStart}–{pageEnd}
                        </span>{' '}
                        of{' '}
                        <span className="font-medium text-[#1e293b]">{totalCount}</span>{' '}
                        blogs
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handlePrevPage}
                            disabled={currentPage === 1}
                            className="px-4 py-2 border border-[#e2e8f0] rounded-lg hover:bg-[#f8fafc] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Previous
                        </button>
                        <span className="px-3 py-2 text-[#1e293b] font-medium">
                            {currentPage} / {totalPages}
                        </span>
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage >= totalPages}
                            className="px-4 py-2 border border-[#e2e8f0] rounded-lg hover:bg-[#f8fafc] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            {deleteConfirmId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#fee2e2] flex items-center justify-center flex-shrink-0">
                                <svg
                                    className="w-5 h-5 text-[#ef4444]"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-base font-semibold text-[#1e293b]">
                                    Delete Blog Post
                                </h3>
                                <p className="text-sm text-[#64748b] mt-0.5">
                                    This action cannot be undone.
                                </p>
                            </div>
                        </div>
                        {blogToDelete && (
                            <p className="text-sm text-[#64748b] bg-[#f8fafc] rounded-lg px-3 py-2">
                                You are about to permanently delete{' '}
                                <span className="font-semibold text-[#1e293b]">
                                    "{blogToDelete.title}"
                                </span>
                                .
                            </p>
                        )}
                        <div className="flex items-center justify-end gap-3 pt-2">
                            <button
                                onClick={handleDeleteCancel}
                                className="px-4 py-2.5 border border-[#e2e8f0] rounded-lg text-[#64748b] hover:bg-[#f8fafc] text-sm font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                className="px-4 py-2.5 bg-[#ef4444] text-white rounded-lg text-sm font-semibold hover:bg-[#dc2626] transition-colors"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toggle Status Confirmation Dialog */}
            {toggleConfirmId && (() => {
                const blog = blogs.find((b) => b._id === toggleConfirmId);
                const isCurrentlyActive = blog?.status === BLOG_STATUS.ACTIVE;
                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isCurrentlyActive ? 'bg-[#fef9c3]' : 'bg-[#dcfce7]'}`}>
                                    {isCurrentlyActive ? (
                                        <svg className="w-5 h-5 text-[#ca8a04]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5 text-[#16a34a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    )}
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-[#1e293b]">
                                        {isCurrentlyActive ? 'Deactivate Blog?' : 'Activate Blog?'}
                                    </h3>
                                    <p className="text-sm text-[#64748b] mt-0.5">
                                        {isCurrentlyActive
                                            ? 'This blog will no longer be visible to users.'
                                            : 'This blog will become visible to users.'}
                                    </p>
                                </div>
                            </div>
                            {blog && (
                                <p className="text-sm text-[#64748b] bg-[#f8fafc] rounded-lg px-3 py-2">
                                    <span className="font-semibold text-[#1e293b]">"{blog.title}"</span>
                                    {' '}will be marked as{' '}
                                    <span className={`font-semibold ${isCurrentlyActive ? 'text-[#64748b]' : 'text-[#10b981]'}`}>
                                        {isCurrentlyActive ? 'Inactive' : 'Active'}
                                    </span>.
                                </p>
                            )}
                            <div className="flex items-center justify-end gap-3 pt-2">
                                <button
                                    onClick={handleToggleStatusCancel}
                                    className="px-4 py-2.5 border border-[#e2e8f0] rounded-lg text-[#64748b] hover:bg-[#f8fafc] text-sm font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleToggleStatusConfirm}
                                    className={`px-4 py-2.5 text-white rounded-lg text-sm font-semibold transition-colors ${
                                        isCurrentlyActive
                                            ? 'bg-[#f59e0b] hover:bg-[#d97706]'
                                            : 'bg-[#10b981] hover:bg-[#059669]'
                                    }`}
                                >
                                    {isCurrentlyActive ? 'Yes, Deactivate' : 'Yes, Activate'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}
